import { useState, useMemo, useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { useCSVDownloader, useQueryList } from '@percent/admin-dashboard/common/hooks'
import { ActionModal, Alert, AsyncSelect, DateRange, Flex, FormField, Icon, Modal, TextInput } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PendingPayrollDeductionsModalProps } from './PendingPayrollDeductionsModal.types'
import styles from './PendingPayrollDeductionsModal.module.scss'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'

const THROTTLING_TIME = 500

export function PendingPayrollDeductionsModal({ open, onClose }: PendingPayrollDeductionsModalProps) {
  const { t } = useTranslation()
  const { donationsService, adminService } = useServices()
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)

  const { values, setFieldValue, setValues, errors, isValid, dirty, resetForm, touched, setFieldTouched } = useFormik<{
    startDate: undefined | Date
    endDate: undefined | Date
    partnerId: string
  }>({
    initialValues: {
      startDate: undefined,
      endDate: undefined,
      partnerId: ''
    },

    validationSchema: () =>
      Yup.object().shape({
        startDate: Yup.date().required('Required'),
        endDate: Yup.date().required('Required'),
        partnerId: Yup.string().required()
      }),
    onSubmit: () => {}
  })

  const defaultOption = useMemo(
    () => ({
      label: t('dialog.pendingPayrollDeductions.form.searchPartners'),
      value: ''
    }),
    [t]
  )

  const [partnersOptions, setPartnersOptions] = useState([defaultOption])
  const [partnerOptionsFilter, setPartnerOptionsFilter] = useState('')

  const [{ dataOrNull, error, isLoading }, { query }] = useQueryList(adminService.queries.getPartnerList, {
    pageSize: 25
  })

  const selectedOption = useMemo(() => {
    return partnersOptions.find(option => option.value === values.partnerId) || partnersOptions[0]
  }, [partnersOptions, values.partnerId])

  useEffect(() => {
    if (!isLoading && !error && dataOrNull && partnerOptionsFilter) {
      const newOptions = dataOrNull.map(({ name, id }) => ({
        label: name,
        value: id
      }))

      const uniqueOptions = [defaultOption, selectedOption, ...newOptions].reduce(
        (accumulator: { label: string; value: string }[], current) => {
          if (!accumulator.find(item => item.value === current.value)) {
            accumulator.push(current)
          }

          return accumulator
        },
        []
      )

      setPartnersOptions(uniqueOptions)
    }
  }, [dataOrNull, defaultOption, error, isLoading, partnerOptionsFilter, selectedOption])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(
    debounce<any>((filter: string) => {
      query({ query: filter })
    }, THROTTLING_TIME),
    []
  )

  useEffect(() => {
    if (partnerOptionsFilter) {
      debouncedQuery(partnerOptionsFilter)
    }
  }, [partnerOptionsFilter, debouncedQuery])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleDateRangeClick = () => {
    setFieldTouched('startDate', true)
    setFieldTouched('endDate', true)
    setIsDatePickerOpened(true)
  }

  const handleDateRangeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleDateRangeClick()
    }
  }

  const [{ loading: isDownloadInProgress }, { downloadCSV }] = useCSVDownloader({
    service: () =>
      donationsService.downloadPayrollDeductionReport({
        partnerId: values.partnerId,
        startDate: dayJS(values.startDate).startOf('day').toISOString(),
        endDate: dayJS(values.endDate).endOf('day').toISOString()
      }),
    fileName: `payroll_deduction_report_${dayjs(new Date()).format('YYYY-MM-DD')}`,
    successMessage: t('toast.exportPayrollDeductionsSuccess')
  })

  const handleDownload = async () => {
    if (downloadCSV) {
      await downloadCSV()
      handleClose()
    }
  }

  const defaultDateRange = useMemo(
    () => ({
      startDate: dayJS().subtract(1, 'day').toDate(),
      endDate: dayJS().subtract(1, 'day').toDate()
    }),
    []
  )

  const dateRangeQueryParams = useMemo(() => {
    if (values.startDate && values.endDate) {
      return {
        startDate: dayJS(values.startDate).format('YYYY-MM-DDTHH:mm:ssZ'),
        endDate: dayJS(values.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
      }
    }

    if (isDatePickerOpened) {
      return {
        startDate: dayJS(defaultDateRange.startDate).format('YYYY-MM-DDTHH:mm:ssZ'),
        endDate: dayJS(defaultDateRange.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
      }
    }

    return {
      startDate: undefined,
      endDate: undefined
    }
  }, [defaultDateRange.endDate, defaultDateRange.startDate, isDatePickerOpened, values.endDate, values.startDate])

  return (
    <Modal open={open} onClose={handleClose}>
      <ActionModal
        title={t('dialog.pendingPayrollDeductions.header')}
        primaryButtonText={t('button.downloadCsv')}
        primaryBtnTestId="pending-payroll-deductions-download-csv"
        secondaryButtonText={t('button.cancel')}
        secondaryBtnTestId="pending-payroll-deductions-cancel"
        handleClose={handleClose}
        handleSubmit={handleDownload}
        disabled={!(isValid && dirty) || isDownloadInProgress}
        loading={isDownloadInProgress}
      >
        <Flex direction="column" gap={16}>
          <Alert variant="info" title={t('dialog.pendingPayrollDeductions.info')} />

          <FormField label={t('dialog.pendingPayrollDeductions.form.selectPartner')} necessity="required">
            <AsyncSelect
              name="partner"
              placeholder={t('dialog.pendingPayrollDeductions.form.searchPartners')}
              loading={isLoading}
              options={partnersOptions.filter(option => option.value !== '')}
              setQuery={q => setPartnerOptionsFilter(q.trim())}
              query={partnerOptionsFilter}
              onChange={e => {
                setFieldTouched('partnerId')
                setFieldValue('partnerId', e?.value || '')
              }}
              defaultValue={selectedOption?.value ? selectedOption : undefined}
              loadingText="Loading..."
              noResultsFoundText={t('errorMessage.noResult')}
              data-testid="partnerSearchableSelect"
            />
          </FormField>

          <FormField
            label={t('dialog.pendingPayrollDeductions.form.dateRange')}
            necessity="required"
            status={
              (touched.startDate || touched.endDate) && (errors.startDate || errors.endDate) ? 'danger' : 'default'
            }
            statusMessage={
              touched.startDate || touched.endDate
                ? ((errors.startDate || errors.endDate) as string | undefined)
                : undefined
            }
          >
            <div className={styles.datePickerContainer}>
              <DateRange
                open={isDatePickerOpened}
                setOpen={setIsDatePickerOpened}
                queryParams={dateRangeQueryParams}
                setQueryParams={params => {
                  if (typeof params === 'object' && params.startDate && params.endDate) {
                    setValues({
                      startDate: dayJS(params.startDate).toDate(),
                      endDate: dayJS(params.endDate).toDate(),
                      partnerId: values.partnerId
                    })
                  }
                }}
                applyButtonText={t('button.apply')}
                withinPortal={false}
                maxDate={dayJS().endOf('day').toDate()}
              >
                <div
                  className={styles.datePickerWrapper}
                  onClick={handleDateRangeClick}
                  onKeyDown={handleDateRangeKeyDown}
                  role="button"
                  tabIndex={0}
                >
                  <TextInput
                    className={styles.datePickerInput}
                    name="date-range"
                    placeholder={t('dialog.pendingPayrollDeductions.form.dateRangePlaceholder')}
                    value={
                      values.startDate && values.endDate
                        ? `${dayJS(values.startDate).format('DD MMM YYYY')} - ${dayJS(values.endDate).format(
                            'DD MMM YYYY'
                          )}`
                        : ''
                    }
                    readOnly
                    tabIndex={-1}
                  />
                  <Icon name="dropdown-arrow-down" size={8} color="gray600" />
                </div>
              </DateRange>
            </div>
          </FormField>
        </Flex>
      </ActionModal>
    </Modal>
  )
}
